import React, { useState } from 'react';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';

// Define types for our component
interface JsonToLatexProps {}

// LaTeX theme options
type LatexTheme = 'basic' | 'colored' | 'compact' | 'detailed';

// LaTeX content display options
type DisplayOption = 'tables' | 'lists' | 'nested';

const JsonToLatex: React.FC<JsonToLatexProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the LaTeX output
  const [latexOutput, setLatexOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('json_data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for LaTeX theme
  const [theme, setTheme] = useState<LatexTheme>('basic');
  // State for display option
  const [displayOption, setDisplayOption] = useState<DisplayOption>('tables');
  // State to track if the conversion happened
  const [isLatexGenerated, setIsLatexGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for LaTeX preview (placeholder for potential future implementation)
  const [showPreview, setShowPreview] = useState<boolean>(false);

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsLatexGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsLatexGenerated(false);
    setLatexOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setLatexOutput('');
    setError('');
    setFileName('json_data');
    setTheme('basic');
    setDisplayOption('tables');
    setIsLatexGenerated(false);
  };

  // LaTeX escape function for special characters
  const escapeLatex = (text: string): string => {
    return text
      .replace(/\\/g, '\\textbackslash{}')
      .replace(/&/g, '\\&')
      .replace(/%/g, '\\%')
      .replace(/\$/g, '\\$')
      .replace(/#/g, '\\#')
      .replace(/_/g, '\\_')
      .replace(/\{/g, '\\{')
      .replace(/\}/g, '\\}')
      .replace(/~/g, '\\textasciitilde{}')
      .replace(/\^/g, '\\textasciicircum{}')
      .replace(/>/g, '\\textgreater{}')
      .replace(/</g, '\\textless{}')
      .replace(/\|/g, '\\textbar{}');
  };

  // Generate LaTeX preamble with necessary packages
  const generateLatexPreamble = (): string => {
    const colorSetup = theme === 'colored' || theme === 'detailed' 
      ? `\\usepackage{xcolor}
% Define colors for JSON elements
\\definecolor{keycolor}{RGB}{0, 86, 179}
\\definecolor{stringcolor}{RGB}{0, 128, 0}
\\definecolor{numbercolor}{RGB}{214, 51, 132}
\\definecolor{booleancolor}{RGB}{156, 39, 176}
\\definecolor{nullcolor}{RGB}{108, 117, 125}
\\definecolor{bracketcolor}{RGB}{73, 80, 87}` 
      : '';

    return `\\documentclass{article}

\\usepackage[utf8]{inputenc}
\\usepackage[T1]{fontenc}
\\usepackage{lmodern}
\\usepackage{textcomp}
\\usepackage{array}
\\usepackage{longtable}
\\usepackage{booktabs}
\\usepackage{enumitem}
\\usepackage{hyperref}
${colorSetup}

\\title{${escapeLatex(fileName || 'JSON Data')} - LaTeX Representation}
\\author{Generated by JSON to LaTeX Converter}
\\date{\\today}

\\begin{document}

\\maketitle

${theme === 'detailed' ? '\\tableofcontents\n\n' : ''}`;
  };

  // Generate LaTeX for a JSON value based on its type
  const generateLatexForValue = (value: any, indentLevel: number = 0): string => {
    if (value === null) {
      return theme === 'colored' || theme === 'detailed' 
        ? '\\textcolor{nullcolor}{null}' 
        : 'null';
    }

    if (typeof value === 'undefined') {
      return theme === 'colored' || theme === 'detailed' 
        ? '\\textcolor{nullcolor}{undefined}' 
        : 'undefined';
    }

    if (typeof value === 'string') {
      const escapedString = escapeLatex(value);
      return theme === 'colored' || theme === 'detailed' 
        ? `\\textcolor{stringcolor}{"${escapedString}"}` 
        : `"${escapedString}"`;
    }

    if (typeof value === 'number') {
      return theme === 'colored' || theme === 'detailed' 
        ? `\\textcolor{numbercolor}{${value}}` 
        : `${value}`;
    }

    if (typeof value === 'boolean') {
      return theme === 'colored' || theme === 'detailed' 
        ? `\\textcolor{booleancolor}{${value}}` 
        : `${value}`;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return theme === 'colored' || theme === 'detailed' 
          ? '\\textcolor{bracketcolor}{[\\,]}' 
          : '[\\,]';
      }

      // Determine if this is a simple array (all primitive values)
      const isSimpleArray = value.every(item => 
        item === null || 
        typeof item !== 'object'
      );

      // For simple arrays, use a simple comma-separated list
      if (isSimpleArray && (theme === 'compact' || displayOption === 'lists')) {
        const items = value.map(item => generateLatexForValue(item));
        return `[${items.join(', ')}]`;
      }

      // For tables display option
      if (displayOption === 'tables' && isSimpleArray) {
        let latexArray = '\\begin{table}[htbp]\n';
        latexArray += '\\centering\n';
        latexArray += '\\begin{tabular}{|c|l|}\n';
        latexArray += '\\hline\n';
        latexArray += 'Index & Value \\\\ \\hline\n';
        
        value.forEach((item, index) => {
          latexArray += `${index} & ${generateLatexForValue(item)} \\\\ \\hline\n`;
        });
        
        latexArray += '\\end{tabular}\n';
        latexArray += `\\caption{Array with ${value.length} items}\n`;
        latexArray += '\\end{table}\n';
        
        return latexArray;
      }

      // For complex arrays or nested display option, use itemize
      let latexArray = '\\begin{itemize}[leftmargin=*]\n';
      
      value.forEach((item, index) => {
        latexArray += `\\item[${index}:] ${generateLatexForValue(item, indentLevel + 1)}\n`;
      });
      
      latexArray += '\\end{itemize}\n';
      
      return latexArray;
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      
      if (keys.length === 0) {
        return theme === 'colored' || theme === 'detailed' 
          ? '\\textcolor{bracketcolor}{\\{\\,\\}}' 
          : '\\{\\,\\}';
      }

      // Determine if this is a simple object (all primitive values)
      const isSimpleObject = keys.every(key => 
        value[key] === null || 
        typeof value[key] !== 'object'
      );

      // For tables display option with simple objects
      if (displayOption === 'tables' && isSimpleObject) {
        let latexObject = '\\begin{table}[htbp]\n';
        latexObject += '\\centering\n';
        latexObject += '\\begin{tabular}{|l|l|}\n';
        latexObject += '\\hline\n';
        latexObject += 'Key & Value \\\\ \\hline\n';
        
        keys.forEach(key => {
          const keyFormatted = theme === 'colored' || theme === 'detailed' 
            ? `\\textcolor{keycolor}{${escapeLatex(key)}}` 
            : escapeLatex(key);
          latexObject += `${keyFormatted} & ${generateLatexForValue(value[key])} \\\\ \\hline\n`;
        });
        
        latexObject += '\\end{tabular}\n';
        latexObject += `\\caption{Object with ${keys.length} properties}\n`;
        latexObject += '\\end{table}\n';
        
        return latexObject;
      }

      // For complex objects or nested display option, use description
      let latexObject = '\\begin{description}\n';
      
      keys.forEach(key => {
        const keyFormatted = theme === 'colored' || theme === 'detailed' 
          ? `\\textcolor{keycolor}{${escapeLatex(key)}}` 
          : escapeLatex(key);
        latexObject += `\\item[${keyFormatted}] ${generateLatexForValue(value[key], indentLevel + 1)}\n`;
      });
      
      latexObject += '\\end{description}\n';
      
      return latexObject;
    }

    return escapeLatex(String(value));
  };

  // Generate document content section
  const generateDocumentContent = (jsonData: any): string => {
    let content = '';
    
    if (theme === 'detailed') {
      content += '\\section{JSON Data Structure}\n\n';
      
      if (typeof jsonData === 'object' && jsonData !== null) {
        if (Array.isArray(jsonData)) {
          content += `This document represents a JSON array with ${jsonData.length} elements.\n\n`;
        } else {
          content += `This document represents a JSON object with ${Object.keys(jsonData).length} properties.\n\n`;
        }
      }
      
      content += '\\section{JSON Content}\n\n';
    }
    
    content += generateLatexForValue(jsonData);
    
    if (theme === 'detailed') {
      content += '\n\\section{Notes}\n\n';
      content += 'This document was automatically generated from JSON data using the JSON to LaTeX converter.\n';
      content += 'Each JSON element is represented in LaTeX with appropriate formatting and structure.\n';
    }
    
    return content;
  };

  // Generate LaTeX document end
  const generateLatexEnd = (): string => {
    return '\\end{document}';
  };

  // Convert JSON to LaTeX
  const convertJsonToLatex = () => {
    setError('');
    setIsLoading(true);
    setIsLatexGenerated(false);
    setLatexOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      const preamble = generateLatexPreamble();
      const content = generateDocumentContent(jsonData);
      const documentEnd = generateLatexEnd();
      
      const fullLatex = `${preamble}${content}${documentEnd}`;
      
      setLatexOutput(fullLatex);
      setIsLatexGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating LaTeX: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy LaTeX to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(latexOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download LaTeX file
  const downloadLatex = () => {
    const blob = new Blob([latexOutput], { type: 'application/x-tex' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'json_data'}.tex`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to LaTeX Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to LaTeX documents. Paste your JSON data below to generate a formatted LaTeX representation suitable for academic papers, reports, and publications.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="theme" className="block text-sm font-medium text-gray-700 mb-1">
                    LaTeX Theme
                  </label>
                  <select
                    id="theme"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={theme}
                    onChange={(e) => setTheme(e.target.value as LatexTheme)}
                  >
                    <option value="basic">Basic</option>
                    <option value="colored">Colored</option>
                    <option value="compact">Compact</option>
                    <option value="detailed">Detailed (with TOC)</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="displayOption" className="block text-sm font-medium text-gray-700 mb-1">
                    Display Style
                  </label>
                  <select
                    id="displayOption"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={displayOption}
                    onChange={(e) => setDisplayOption(e.target.value as DisplayOption)}
                  >
                    <option value="tables">Tables (for simple objects/arrays)</option>
                    <option value="lists">Lists</option>
                    <option value="nested">Nested (description/itemize)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Determines how JSON objects and arrays are represented in LaTeX
                  </p>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToLatex}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to LaTeX'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              LaTeX Output
            </label>
            
            {isLatexGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy LaTeX'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadLatex}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download .tex
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isLatexGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    LaTeX output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isLatexGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="flex-grow flex flex-col">
                  <label htmlFor="latexOutput" className="text-sm font-medium text-gray-700 mb-1">
                    LaTeX Code:
                  </label>
                  <textarea
                    id="latexOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-xs focus:ring-blue-500 focus:border-blue-500"
                    value={latexOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isLatexGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">LaTeX Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated LaTeX can be compiled using any LaTeX editor or compiler like Overleaf, TeXLive, or MiKTeX.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to LaTeX Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your JSON data into LaTeX documents. The converter preserves your JSON structure
              while formatting it appropriately for LaTeX documents, making it suitable for inclusion in academic
              papers, reports, and publications.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Structured Formatting</strong> - Properly formatted JSON data using LaTeX environments</li>
              <li><strong>Multiple Themes</strong> - Choose from basic, colored, compact, or detailed themes</li>
              <li><strong>Display Options</strong> - Represent JSON objects and arrays as tables, lists, or nested structures</li>
              <li><strong>Special Character Handling</strong> - Proper escaping of LaTeX special characters</li>
              <li><strong>LaTeX Compatibility</strong> - Generated code works with standard LaTeX compilers</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>Choose the "Colored" theme for better readability when viewing the final PDF</li>
              <li>For complex nested structures, use the "Nested" display option</li>
              <li>For simple data, the "Tables" display option often produces the most readable results</li>
              <li>The "Detailed" theme adds a table of contents and additional sections, suitable for longer documents</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToLatex;